import React, { useMemo } from 'react';
import { format } from 'date-fns';
import '../../styles/employee-profile-workspace.css';

const renderStars = (value = 0) => {
  const roundedValue = Math.round(Number(value) || 0);

  return Array.from({ length: 5 }, (_, index) => {
    const starNumber = index + 1;
    return (
      <span
        key={starNumber}
        className={`employee-ratings__star ${starNumber <= roundedValue ? 'is-filled' : ''}`}
        aria-hidden="true"
      >
        ★
      </span>
    );
  });
};

const formatRatingDate = (createdAt) => {
  if (!createdAt) return 'Date unavailable';

  if (createdAt?.seconds) {
    return format(new Date(createdAt.seconds * 1000), 'MMM d, yyyy');
  }

  const parsedDate = new Date(createdAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Date unavailable';
  }

  return format(parsedDate, 'MMM d, yyyy');
};

const EmployeeRatingsSection = ({ ratings = [] }) => {
  const sortedRatings = useMemo(() => {
    if (!Array.isArray(ratings)) return [];

    return [...ratings].sort((a, b) => {
      const aTime = a?.createdAt?.seconds ? a.createdAt.seconds : new Date(a?.createdAt || 0).getTime() / 1000;
      const bTime = b?.createdAt?.seconds ? b.createdAt.seconds : new Date(b?.createdAt || 0).getTime() / 1000;
      return bTime - aTime;
    });
  }, [ratings]);

  const averageRating = useMemo(() => {
    if (!sortedRatings.length) return 0;

    const total = sortedRatings.reduce((sum, item) => sum + Number(item?.rating || 0), 0);
    return total / sortedRatings.length;
  }, [sortedRatings]);

  if (!sortedRatings.length) {
    return (
      <section className="employee-workspace__placeholder">
        <div className="employee-workspace__placeholderInner">
          <h2 className="employee-workspace__placeholderTitle">Ratings</h2>
          <p className="employee-workspace__placeholderText">
            This employee has not received any ratings yet.
          </p>
        </div>
      </section>
    );
  }

  return (
    <div className="employee-ratings">
      <section className="employee-workspace__panel employee-ratings__summary">
        <h2 className="employee-workspace__panelTitle">Ratings Overview</h2>
        <div className="employee-ratings__summaryValue">{averageRating.toFixed(1)}</div>
        <div
          className="employee-ratings__stars"
          aria-label={`${averageRating.toFixed(1)} out of 5 stars`}
        >
          {renderStars(averageRating)}
        </div>
        <p className="employee-workspace__panelSubtitle">
          Based on {sortedRatings.length} {sortedRatings.length === 1 ? 'review' : 'reviews'}
        </p>
      </section>

      <section className="employee-workspace__panel">
        <h2 className="employee-workspace__panelTitle">All Reviews</h2>
        <div className="employee-ratings__list">
          {sortedRatings.map((rating, index) => (
            <article
              key={rating.appointmentId || `${rating.clientName || 'rating'}-${index}`}
              className="employee-ratings__item"
            >
              <div className="employee-ratings__stars" aria-label={`${rating.rating} out of 5 stars`}>
                {renderStars(rating.rating)}
              </div>

              {rating.comment ? (
                <p className="employee-ratings__comment">{rating.comment}</p>
              ) : (
                <p className="employee-ratings__comment employee-ratings__comment--muted">
                  No written review left.
                </p>
              )}

              <div className="employee-ratings__meta">
                <span>{rating.clientName || 'Anonymous'}</span>
                <span className="employee-ratings__dot">•</span>
                <span>{rating.serviceName || 'Service unavailable'}</span>
                <span className="employee-ratings__dot">•</span>
                <span>{formatRatingDate(rating.createdAt)}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default EmployeeRatingsSection;
